/**
 * NIGHTBOUND - modal dialog.
 *
 * A real dialog: focus is moved in, Escape closes, background scroll is locked,
 * and the backdrop is a labelled close control. Rendered into a portal so it is
 * never trapped inside a scrolling panel.
 */

import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cx } from './ui'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  /** On phones the panel slides up from the bottom as a sheet. */
  sheetOnMobile?: boolean
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  sheetOnMobile = true,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const descriptionId = useId()

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panelRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div
      className="nb-fade-in fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      role="presentation"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-void/80 backdrop-blur-[2px]"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...(description ? { 'aria-describedby': descriptionId } : {})}
        tabIndex={-1}
        className={cx(
          'nb-panel relative z-10 max-h-[92svh] w-full overflow-hidden outline-none',
          sheetOnMobile
            ? 'nb-sheet-in rounded-b-none rounded-t-[6px] sm:nb-modal-in sm:rounded-[4px]'
            : 'nb-modal-in rounded-[4px]',
          'sm:max-w-lg',
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-ash-700/50 px-4 py-3">
          <div>
            <h2 id={titleId} className="font-display text-lg tracking-wide text-bone-200">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="mt-1 text-xs leading-relaxed text-ash-400">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 -mt-1 rounded-[3px] px-2 py-1 text-lg leading-none text-ash-400 hover:bg-night-700 hover:text-bone-200"
          >
            ×
          </button>
        </div>

        <div className="nb-scroll-thin max-h-[62svh] overflow-y-auto px-4 py-4">{children}</div>

        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-ash-700/50 bg-night-950/40 px-4 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
